import {
    QueryClient,
    useQuery,
    useQueryClient,
    useMutation,
} from '@tanstack/react-query';
import {
    AGENT_DEPARTURE_HYPOTHESIS_TYPE,
    AgentDepartureHypothesisType,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/constants';
import {
    AgentDepartureHypothesis,
    DetailedAgentDepartureHypothesis,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/types';

import { resetResultSimulationDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import { START_DATE } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';

import {
    RQAPICallWithData,
    HttpResponse,
    HttpContentWithData,
} from 'store/APIClient';
import { dispatch } from 'store/store';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { useTranslation } from 'react-i18next';

export type AgentDepartureHypothesisMutationParams = {
    [AGENT_DEPARTURE_HYPOTHESIS_TYPE]: AgentDepartureHypothesisType;
    [START_DATE]: SimulationDateString;
};

const AGENT_DEPARTURE_HYPOTHESES_API_KEY = 'agent_departure_hypotheses';
const DETAILED_AGENT_DEPARTURE_HYPOTHESES_API_KEY =
    'detailed_agent_departure_hypotheses';

const invalidateDetailedAgentDepartureHypothesesQueries = (
    queryClient: QueryClient,
    hrSimulationId: number,
) => {
    queryClient.invalidateQueries({
        queryKey: [DETAILED_AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],
    });
};

export const resetAgentDepartureHypothesesQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
) => {
    queryClient.resetQueries({
        queryKey: [AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],
    });
    queryClient.resetQueries({
        queryKey: [DETAILED_AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],
    });
};

export const useGetAgentDepartureHypotheses = (hrSimulationId: number) =>
    useQuery({
        queryKey: [AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                AgentDepartureHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/agent_departure_hypotheses`,
            });
            return response.content.data;
        },
    });

export const useGetDetailedAgentDepartureHypotheses = (
    hrSimulationId: number,
) =>
    useQuery({
        queryKey: [DETAILED_AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                DetailedAgentDepartureHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/detailed_agent_departure_hypotheses`,
            });
            return response.content.data;
        },
    });

const useCreateAgentDepartureHypothesisMutation = (
    hrSimulationId: number,
    onAgentDepartureHypothesisCreated: () => void,
    recurrence: RecurrenceEnum,
    mantyStatus?: string,
    agentSoftwareId?: string,
    jobNumber?: string | null,
) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    return useMutation({
        mutationFn: ({
            startDate,
            agentDepartureHypothesisType,
        }: AgentDepartureHypothesisMutationParams) => {
            if (!agentSoftwareId || jobNumber === undefined) {
                return {} as Promise<
                    HttpResponse<HttpContentWithData<AgentDepartureHypothesis>>
                >;
            }
            return RQAPICallWithData<AgentDepartureHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/agent_departure_hypotheses`,
                method: 'POST',
                body: {
                    mantyStatus,
                    agentSoftwareId,
                    jobNumber,
                    startDate,
                    agentDepartureHypothesisType,
                },
            });
        },

        onSuccess: (response) => {
            onAgentDepartureHypothesisCreated();
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                [AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: AgentDepartureHypothesis[]) => [
                    ...oldData,
                    response.content.data,
                ],
            );
            invalidateDetailedAgentDepartureHypothesesQueries(
                queryClient,
                hrSimulationId,
            );
            const eventName =
                recurrence === RecurrenceEnum.RECURRENT
                    ? TRACKABLE_EVENTS.human_resources.simulation.recurrent
                          .createAgentDepartureHypothesis
                    : TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                          .createAgentDepartureHypothesis;
            trackEventPRH(eventName, {
                type: t(
                    `humanResources.simulation.agentDepartureHypothesisType.${response.content.data.agentDepartureHypothesisType}`,
                ),
                mantyStatus,
            });
        },
        meta: { useGlobalOnError: true },
    });
};

const useDeleteAgentDepartureHypothesisMutation = (
    hrSimulationId: number,
    onAgentDepartureHypothesisDeleted: () => void,
    hypothesisId?: number,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            RQAPICallWithData({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/agent_departure_hypothesis/${hypothesisId}`,
                method: 'DELETE',
            }),

        onSuccess: (response) => {
            onAgentDepartureHypothesisDeleted();
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                [AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: AgentDepartureHypothesis[]) =>
                    oldData.filter(
                        (hypothesis) => hypothesis.id !== hypothesisId,
                    ),
            );
        },
        meta: { useGlobalOnError: true },
    });
};

const useUpdateAgentDepartureHypothesisMutation = (
    hrSimulationId: number,
    onAgentDepartureHypothesisUpdated: () => void,
    hypothesisId?: number,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            startDate,
            agentDepartureHypothesisType,
        }: AgentDepartureHypothesisMutationParams) => {
            if (hypothesisId === undefined) return undefined;

            return RQAPICallWithData<AgentDepartureHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/agent_departure_hypothesis/${hypothesisId}`,
                method: 'PUT',
                body: { startDate, agentDepartureHypothesisType },
            });
        },

        onSuccess: (response) => {
            onAgentDepartureHypothesisUpdated();
            dispatch.vision.notifyUser({
                message: response?.content.message ?? '',
                snackType: 'success',
            });

            const responseData = response?.content.data;
            queryClient.setQueryData(
                [AGENT_DEPARTURE_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: AgentDepartureHypothesis[]) =>
                    oldData.map((hypothesis) =>
                        hypothesis.id === hypothesisId && responseData
                            ? responseData
                            : hypothesis,
                    ),
            );
        },
        meta: { useGlobalOnError: true },
    });
};

export const useAgentDepartureHypothesisCRUDMutations = ({
    simulationId,
    recurrence,
    jobNumber,
    agentSoftwareId,
    mantyStatus,
    hypothesisId,
}: {
    simulationId: number;
    recurrence: RecurrenceEnum;
    jobNumber?: string | null;
    agentSoftwareId?: string;
    mantyStatus?: string;
    hypothesisId?: number;
}) => {
    const { setHasChanges } = useHypothesesChangesContext();
    const queryClient = useQueryClient();
    const onSuccess = () => {
        invalidateDetailedAgentDepartureHypothesesQueries(
            queryClient,
            simulationId,
        );
        // as we have hypotheses changes, we reset the result queries
        resetResultSimulationDataQueries(queryClient, simulationId);
        setHasChanges(true);
    };

    const { mutate: createHypothesis, isPending: createLoading } =
        useCreateAgentDepartureHypothesisMutation(
            simulationId,
            onSuccess,
            recurrence,
            mantyStatus,
            agentSoftwareId,
            jobNumber,
        );
    const { mutate: editHypothesis, isPending: editLoading } =
        useUpdateAgentDepartureHypothesisMutation(
            simulationId,
            onSuccess,
            hypothesisId,
        );
    const { mutate: deleteHypothesis, isPending: deleteLoading } =
        useDeleteAgentDepartureHypothesisMutation(
            simulationId,
            onSuccess,
            hypothesisId,
        );

    return {
        createHypothesis,
        editHypothesis,
        deleteHypothesis,
        isHypothesisMutationLoading:
            createLoading || editLoading || deleteLoading,
    };
};
